import { refusalOf } from "api/refusal";
import { isSessionFailure } from "api/SessionAuth";
import { TelegramOidcError } from "api/TelegramAuth";
import type { TelegramPopupFailure } from "hooks/useTelegramOidcPopup";
import { isEmailShape } from "utils/validation/emailValidation";

/** The message id for a failed Google link. Anything unrecognised, a network
 *  error included, reads as "temporarily unavailable", never as a success. */
export const googleLinkErrorId = (error: unknown): string => {
  const { status, detail } = refusalOf(error);
  if (status === 401) return "signInMethods.error.sessionExpired";
  if (status === 400) return "signInMethods.error.invalidGoogleToken";
  if (status === 409 && detail === "taken") return "signInMethods.error.googleTaken";
  if (status === 409 && detail === "already_linked_self") {
    return "signInMethods.error.googleAlreadyLinked";
  }
  if (status === 409 && detail === "account_mismatch") {
    return "signInMethods.error.accountMismatch";
  }
  return "signInMethods.error.unavailable";
};

/** The message id for a refused Telegram link. A 401 that is not about the
 *  session is the token's: expired, used, or its nonce is gone. A failed refresh
 *  arrives as its own axios 401, and is about the session. */
export const telegramLinkErrorId = (error: unknown): string => {
  const { status, detail } = refusalOf(error);
  if (status === 401) {
    return error instanceof TelegramOidcError && !isSessionFailure(error)
      ? "signInMethods.error.telegramExpired"
      : "signInMethods.error.sessionExpired";
  }
  if (status === 409 && detail === "taken") return "signInMethods.error.telegramTaken";
  if (status === 409 && detail === "already_linked_self") {
    return "signInMethods.error.telegramAlreadyLinked";
  }
  if (status === 409 && detail === "account_mismatch") {
    return "signInMethods.error.accountMismatch";
  }
  if (status === 429) return "TelegramLoginRateLimited";
  return "signInMethods.error.unavailable";
};

/** The message id for a failure before any link call was made. */
export const telegramPopupErrorId = (
  phase: TelegramPopupFailure["phase"],
  error: unknown
): string => {
  if (phase === "token") return "TelegramLoginFailed";
  return refusalOf(error).status === 429
    ? "TelegramLoginRateLimited"
    : "signInMethods.error.unavailable";
};

// users.email is varchar(63).
export const EMAIL_MAX_LENGTH = 63;

/** The message id for an address that must not be submitted, or null. */
export const emailAddressErrorId = (address: string): string | null => {
  const trimmed = address.trim();
  if (trimmed.length > EMAIL_MAX_LENGTH) return "signInMethods.email.tooLong";
  return isEmailShape(trimmed) ? null : "signInMethods.email.invalid";
};

/** What a refused change_email means for the dialog. 422 is FastAPI's own
 *  refusal of a shape its validator rejects, before the route runs. */
export const changeEmailOutcome = (
  error: unknown
): { messageId: string; inline?: boolean; alreadySet?: boolean } => {
  const { status, detail } = refusalOf(error);
  if (status === 400 || status === 422) {
    return { messageId: "signInMethods.email.invalid", inline: true };
  }
  if (status === 401) return { messageId: "signInMethods.error.sessionExpired" };
  if (status === 409 && detail === "email_already_set") {
    return { messageId: "signInMethods.email.alreadySet", alreadySet: true };
  }
  if (status === 409 && detail === "account_mismatch") {
    return { messageId: "signInMethods.error.accountMismatch" };
  }
  if (status === 429) return { messageId: "signInMethods.email.rateLimited" };
  return { messageId: "signInMethods.error.unavailable" };
};
